import React, { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import _ from 'lodash';
import classNames from 'classnames';
import { ResponsiveType } from 'react-multi-carousel';
import { Carousel, ICarouselOptions } from '@kursk/components/ui/Carousel/Carousel';
import { CarouselButtonGroup } from './CarouselButtonGroup/CarouselButtonGroup';
import styles from './DateFilter.module.scss';
import { CalendarIcon, CloseIcon, CloseIconSmall } from '@kursk/components/ui/icons';
import { themeValue } from "@kursk/themes";
import { BelCalendarIcon } from "@kursk/components/ui/icons.bel";
import { CalendarIcon as CalendarIconVladimir } from "@kursk/components/ui/icons.vladimir";
import { CalendarIcon as CalendarIconPprog } from "@kursk/components/ui/icons.pprog";
import { CalendarIcon as CalendarIconSochi } from "@kursk/components/ui/icons.sochi";
import { CalendarIcon as CalendarIconLnr } from "@kursk/components/ui/icons.lnr";
import { CalendarIcon as CalendarIconAstrakhan } from "@kursk/components/ui/icons.astrakhan";
import { CalendarDoubleDatePicker } from "@kursk/components/ui/CalendarDoubleDatePicker/CalendarDoubleDatePicker";
import { isStartDateInterim } from '@common/query/queryFilter';

interface IProps {
  // eslint-disable-next-line @typescript-eslint/ban-types
  onChange: Function;
  leftDatesDefault?: number;
  rightDatesDefault?: number;
  dateStart?: moment.Moment;
  withoutInterim?: boolean;
  isOpenMobileCarousel?: boolean;
  enableDates?: number[];
  noBorder?: boolean;
  disablePastDatesOnCalendar?: boolean;
  filters: {
    start?: number;
    end?: number;
    interim?: string;
  };
}

const interims = [
  { name: 'today', title: 'сегодня' },
  { name: 'tomorrow', title: 'завтра' },
  { name: 'weekend', title: 'выходные' },
];

const momentFormatDate = (date) => +moment(date).format('DDMMYYYY');

export function DateFilter({
  onChange,
  filters,
  leftDatesDefault,
  rightDatesDefault,
  withoutInterim,
  dateStart,
  enableDates,
  isOpenMobileCarousel,
  noBorder,
  disablePastDatesOnCalendar,
}: IProps): ReactElement {
  // Indent from left and right sides for paginate new dates
  const PAGINATION_INDENT = 15;
  // items count for pagination left\right
  const PAGINATION_ITEMS_COUNT = 30;
  // items in slide on desktop
  const ITEMS_ON_SLIDE = 18;

  const [startDate, setStartDate] = useState<number>(filters.start || null);
  const [endDate, setEndDate] = useState<number>(filters.end || null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [hoveredDate, setHoveredDate] = useState(undefined);
  const [openMobileCarousel, setOpenMobileCarousel] = useState(isOpenMobileCarousel || false);
  const [isHiddenCarousel, setIsHiddenCarousel] = useState(true);
  const rightDates = useRef((() => {
    if (_.isNumber(rightDatesDefault)) {
      if (rightDatesDefault < ITEMS_ON_SLIDE) {
        return ITEMS_ON_SLIDE;
      }
      return rightDatesDefault;
    } else {
      return ITEMS_ON_SLIDE;
    }
  })());
  const leftDates = useRef(_.isNumber(leftDatesDefault) ?
    leftDatesDefault : PAGINATION_ITEMS_COUNT + PAGINATION_ITEMS_COUNT);
  const carouselRef = useRef(null);
  const leftAnchorRef = useRef(null);
  const rightAnchorRef = useRef(null);
  const defaultSlide = useRef((_.isNumber(leftDatesDefault) ||
    _.isNumber(rightDatesDefault)) ? 0 : 60);
  const getCarouselRef = (ref) => {
    if (!carouselRef.current) carouselRef.current = ref;
  }
  const currentInterim = isStartDateInterim(filters.start, filters.end) || '';
  const calendarRef = useRef(null);

  useEffect(() => {
    let observerRight: undefined | IntersectionObserver;
    let observerLeft: undefined | IntersectionObserver;
    if (leftAnchorRef.current && rightAnchorRef.current) {
      observerRight = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          rightDates.current += PAGINATION_ITEMS_COUNT;
        }
      }, {
        root: carouselRef.current.containerRef.current
      });
      observerLeft = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          // тут переносим слайд на следующий, до дабавления следующих 30 элементов, чтобы не засветить якорь
          // это происходит до рендера,
          // перерендер карусели произойдет после смены слайда на событии afterChange
          carouselRef.current.state.currentSlide += PAGINATION_ITEMS_COUNT;
          leftDates.current += PAGINATION_ITEMS_COUNT;
        }
      }, {
        root: carouselRef.current.containerRef.current
      });
      observerLeft.observe(leftAnchorRef.current);
      observerRight.observe(rightAnchorRef.current);
    }
    return () => {
      if (observerRight && observerLeft && rightAnchorRef.current && leftAnchorRef.current) {
        observerRight.unobserve(rightAnchorRef.current);
        observerLeft.unobserve(leftAnchorRef.current);
      }
    }
  }, [leftAnchorRef.current, rightAnchorRef.current, carouselRef.current]);

  const onDateClick = async (date: number) => {
    let newStart = startDate;
    let newEnd = endDate;

    if (!startDate && !endDate) {
      newStart = date;
    } else if (startDate === date && !endDate) {
      newStart = null;
    } else if (startDate && !endDate) {
      if (startDate > date) {
        newEnd = startDate;
        newStart = date;
      } else {
        newEnd = date;
      }
    } else if (startDate && endDate) {
      newStart = date;
      newEnd = null;
    }

    setStartDate(newStart);
    setEndDate(newEnd);

    await onChange({ start: newStart, end: newEnd });
  }

  const onInterimClick = async (interimName: string) => {
    let newStart = startDate;
    let newEnd = endDate;
    const now = moment().startOf('day');

    /* eslint-disable indent */
    switch (interimName) {
      case currentInterim:
        newStart = null;
        newEnd = null;
        break;

      case 'today':
        newStart = now.valueOf();
        newEnd = null;
        break;

      case 'tomorrow':
        newStart = now.add(1, 'day').valueOf();
        newEnd = null;
        break;

      case 'weekend':
        if (now.day() === 6) {
          newStart = now.valueOf();
          newEnd = now.add(1, 'day').valueOf();
        } else if (now.day() === 0) {
          newStart = now.valueOf();
          newEnd = null;
        } else {
          newStart = now.day(6).valueOf();
          newEnd = now.day(7).valueOf();
        }
        break;
    }
    /* eslint-enable indent */

    setStartDate(newStart);
    setEndDate(newEnd);

    await onChange({ start: newStart, end: newEnd });
  }

  const allDatesMemo = useMemo(() => {
    const startIncrement = dateStart || moment().startOf('day');
    const startDecrement = moment().clone().startOf('day');
    return [
      ..._.times(leftDates.current, () => startDecrement.add(-1, 'day').valueOf()).reverse(),
      startIncrement.valueOf(),
      ..._.times(rightDates.current, () => startIncrement.add(1, 'day').valueOf())
    ];
  },
  [rightDates.current, leftDates.current]);

  const getMonthTitle = (index: number) => {
    const date = allDatesMemo[index];
    if (index === currentSlide) {
      return moment(date).format('MMMM');
    }
    if (Number(moment(date).format('DD')) === 1) {
      return moment(date).format('MMMM');
    }
    return null;
  };

  const renderDate = (date: number, index: number) => {
    const dateMoment = moment(date);
    const isSelected = _.includes([+startDate, +endDate], date);
    const isRange = () => {
      if (hoveredDate && startDate && !endDate) {
        return (date <= hoveredDate && date > startDate) || (date >= hoveredDate && date < startDate);
      }
      return (startDate && endDate && date < endDate && date > startDate);
    };
    const monthTitle = getMonthTitle(index);

    return (
      <button
        key={date}
        data-date={date}
        className={classNames(styles.date)}
        onClick={() => onDateClick(date)}
        onMouseEnter={(el) => {
          setHoveredDate((el.target as any).parentNode.dataset.date || (el.target as any).dataset.date);
        }}
        onMouseLeave={() => setHoveredDate(undefined)}
        disabled={enableDates && !enableDates.find((day) =>
          momentFormatDate(date) === momentFormatDate(day) )}
      >
        {monthTitle && <div className={styles.monthTitle}>{monthTitle}</div>}
        <div className={classNames(
          styles.dateBlock,
          isSelected && styles.dateSelected,
          isRange() && styles.dateRange
        )}>
          <div className={styles.dateMonth}>
            {dateMoment.format('DD')}
          </div>
          <div className={styles.dateWeek}>
            {dateMoment.format('dd')}
          </div>
        </div>
        {(!_.isNumber(leftDatesDefault) || !_.isNumber(rightDatesDefault)) && (
          <>
            {(index === PAGINATION_INDENT) && (
              <div className={styles.anchor} ref={leftAnchorRef}/>
            )}
            {(index === allDatesMemo.length - PAGINATION_INDENT) && (
              <div className={styles.anchor} ref={rightAnchorRef}/>
            )}
          </>
        )}
      </button>
    );
  };

  const allDatesRender = _.map(allDatesMemo, renderDate);

  const responsive: ResponsiveType = {
    desktop: {
      breakpoint: { max: 3000, min: 1080 },
      items: 18
    },
    tablet: {
      breakpoint: { max: 1199 , min: 768 },
      items: 12,
      partialVisibilityGutter: 128
    }
  };

  const options: ICarouselOptions = {
    responsive,
    swipeable: true,
    infinite: false,
    renderButtonGroupOutside: true,
    customButtonGroup: <CarouselButtonGroup
      className={withoutInterim ? styles.buttonsWithoutInterim : styles.buttons}
    />,
    slidesToSlide: 6,
    afterChange: (_, { currentSlide }) => {
      setCurrentSlide(currentSlide)
    },
    itemClass: styles.dateItem
  };

  const mobileOptions: ICarouselOptions = {
    responsive: {
      mobile: {
        breakpoint: { max: 767, min: 0 },
        items: 6,
        partialVisibilityGutter: 50
      }
    },
    swipeable: true,
    infinite: false,
    arrows: false,
    slidesToSlide: 6,
    afterChange: (_, { currentSlide }) => {
      setCurrentSlide(currentSlide)
    },
  };

  const isDateChosen = Boolean(startDate);

  const getChosenDates = () => {
    const format = 'D MMMM';
    return _.reject([
      startDate && moment(startDate).format(format),
      endDate && moment(endDate).format(format)
    ], _.isNull).join(' - ');
  };

  const resetDates = async () => {
    setStartDate(null);
    setEndDate(null);

    await onChange({ start: null, end: null, interim: '' });
  };

  useEffect(() => {
    carouselRef.current.goToSlide(defaultSlide.current);
    setIsHiddenCarousel(false);
  }, [carouselRef, openMobileCarousel]);

  useEffect(() => {
    setStartDate(filters.start);
    setEndDate(filters.end);
  }, [filters]);

  const clearFilter = async() => {
    const slide = allDatesMemo.findIndex((el) => el === dateStart?.valueOf() ||
      el === moment().startOf('day').valueOf());
    if (slide !== -1) {
      setCurrentSlide(slide);
      carouselRef.current.goToSlide(slide);
    }
    await onChange({ start: null, end: null });
  };

  const onCalendarSubmit = async (dates: number[]) => {
    const [startDate, endDate] = dates;
    const slide = allDatesMemo.findIndex((el) => el === startDate);
    if (slide !== -1) {
      let goToSlice = slide;
      // Не даем слайдеру уйти за пределы видимости
      if (goToSlice + ITEMS_ON_SLIDE > allDatesMemo.length) {
        goToSlice = allDatesMemo.length - ITEMS_ON_SLIDE;
      }
      setCurrentSlide(goToSlice);
      carouselRef.current.goToSlide(goToSlice);
    }
    await onChange({ start: startDate, end: endDate });
  };

  return (
    <div className={classNames(styles.wrapper, noBorder && styles.noBorder, openMobileCarousel && styles.mobileOpened)}>
      <div
        className={classNames([
          styles.carousel,
          styles.withCalendar,
          isHiddenCarousel && styles.hide
        ])}>
        {Boolean(!startDate) && (
          <CalendarDoubleDatePicker
            ref={calendarRef}
            enabledDays={enableDates}
            date={dateStart?.valueOf() || moment().startOf('day').valueOf()}
            onChangeDate={onCalendarSubmit}
            disablePastDates={disablePastDatesOnCalendar}
            lastDate={allDatesMemo[allDatesMemo.length - 1]}
            customInput={({ isOpen }) => (
              <div>
                <div className={styles.svgIcon} onClick={(event) => {
                  if (isOpen) {
                    calendarRef?.current && calendarRef.current.setOpen(false);
                    event.stopPropagation()
                  }
                }}>
                  {themeValue({
                    bel: <BelCalendarIcon />,
                    vladimir: <CalendarIconVladimir />,
                    pprog: <CalendarIconPprog />,
                    sochi: <CalendarIconSochi />,
                    lnr: <CalendarIconLnr />,
                    astrakhan: <CalendarIconAstrakhan />,
                    default: <CalendarIcon width={14} />,
                  })}
                </div>
              </div>
            )}
          />
        )}
        {Boolean(startDate) && (
          <div className={styles.svgClear} onClick={() => clearFilter()}>
            <CloseIconSmall width={14}/>
          </div>
        )}
        <Carousel
          carouselRef={getCarouselRef}
          carouselOptions={options}
        >
          {allDatesRender}
        </Carousel>
      </div>
      {!withoutInterim && (
        <div className={styles.presets}>
          {_.map(interims, ({ name, title }) =>
            <a
              key={name}
              className={classNames(styles.preset, currentInterim === name && styles.activePreset)}
              onClick={() => onInterimClick(name)}
            >
              {title}
            </a>
          )}
          <button
            className={
              classNames(
                styles.calendar,
                isDateChosen && styles.calendarBlack
              )}
            onClick={() => setOpenMobileCarousel(!openMobileCarousel)}
          >
            {themeValue({
              bel: <BelCalendarIcon />,
              vladimir: <CalendarIconVladimir />,
              pprog: <CalendarIconPprog />,
              sochi: <CalendarIconSochi />,
              lnr: <CalendarIconLnr />,
              astrakhan: <CalendarIconAstrakhan />,
              default: <CalendarIcon />
            })}
          </button>
        </div>
      )}
      {!openMobileCarousel && isDateChosen && (
        <div className={styles.chosenDates}>
          {getChosenDates()}
          <button className={styles.resetDatesButton} onClick={resetDates}>
            <CloseIcon width={9} height={9} />
          </button>
        </div>
      )}
      {openMobileCarousel && (
        <div className={styles.mobileCarousel}>
          <Carousel
            carouselRef={getCarouselRef}
            carouselOptions={mobileOptions}
          >
            {allDatesRender}
          </Carousel>
        </div>
      )}
    </div>
  );
}
